/**
 * Shared Lucide icon name registry for SEO report generated components.
 *
 * This is the single source of truth for which icon names the LLM can pick
 * and the frontend can render. Both the API (prompt + validation) and the
 * web (render map) import from here.
 *
 * To add a new icon:
 * 1. Add the kebab-case name and PascalCase import name here
 * 2. Add the corresponding import + IMPORT_MAP entry in SeoReportHowWePicked.tsx
 * 3. That's it -- the API prompt and validation pick it up automatically
 */

export interface LucideIconEntry {
  /** kebab-case name used in prompts and stored in generated data */
  name: string
  /** PascalCase name matching the lucide-react export */
  importName: string
}

/**
 * Curated set of ~300 Lucide icons available for LLM selection in generated
 * components. Covers product features, evaluation criteria, and attributes
 * relevant to e-commerce buying guides.
 */
export const LUCIDE_ICON_REGISTRY: LucideIconEntry[] = [
  { name: 'activity', importName: 'Activity' },
  { name: 'alarm-clock', importName: 'AlarmClock' },
  { name: 'alarm-smoke', importName: 'AlarmSmoke' },
  { name: 'antenna', importName: 'Antenna' },
  { name: 'apple', importName: 'Apple' },
  { name: 'armchair', importName: 'Armchair' },
  { name: 'audio-lines', importName: 'AudioLines' },
  { name: 'audio-waveform', importName: 'AudioWaveform' },
  { name: 'award', importName: 'Award' },
  { name: 'backpack', importName: 'Backpack' },
  { name: 'badge', importName: 'Badge' },
  { name: 'badge-check', importName: 'BadgeCheck' },
  { name: 'badge-dollar-sign', importName: 'BadgeDollarSign' },
  { name: 'badge-percent', importName: 'BadgePercent' },
  { name: 'ban', importName: 'Ban' },
  { name: 'bandage', importName: 'Bandage' },
  { name: 'banknote', importName: 'Banknote' },
  { name: 'barcode', importName: 'Barcode' },
  { name: 'bath', importName: 'Bath' },
  { name: 'battery', importName: 'Battery' },
  { name: 'battery-charging', importName: 'BatteryCharging' },
  { name: 'battery-full', importName: 'BatteryFull' },
  { name: 'battery-low', importName: 'BatteryLow' },
  { name: 'battery-medium', importName: 'BatteryMedium' },
  { name: 'beer', importName: 'Beer' },
  { name: 'bell', importName: 'Bell' },
  { name: 'biceps-flexed', importName: 'BicepsFlexed' },
  { name: 'bike', importName: 'Bike' },
  { name: 'binoculars', importName: 'Binoculars' },
  { name: 'bird', importName: 'Bird' },
  { name: 'blinds', importName: 'Blinds' },
  { name: 'bluetooth', importName: 'Bluetooth' },
  { name: 'bluetooth-connected', importName: 'BluetoothConnected' },
  { name: 'bolt', importName: 'Bolt' },
  { name: 'bookmark', importName: 'Bookmark' },
  { name: 'box', importName: 'Box' },
  { name: 'boxes', importName: 'Boxes' },
  { name: 'brain', importName: 'Brain' },
  { name: 'briefcase', importName: 'Briefcase' },
  { name: 'building', importName: 'Building' },
  { name: 'bus', importName: 'Bus' },
  { name: 'cable', importName: 'Cable' },
  { name: 'camera', importName: 'Camera' },
  { name: 'car', importName: 'Car' },
  { name: 'cctv', importName: 'Cctv' },
  { name: 'check', importName: 'Check' },
  { name: 'check-check', importName: 'CheckCheck' },
  { name: 'chef-hat', importName: 'ChefHat' },
  { name: 'circle-check', importName: 'CircleCheck' },
  { name: 'circle-check-big', importName: 'CircleCheckBig' },
  { name: 'circle-dollar-sign', importName: 'CircleDollarSign' },
  { name: 'circle-help', importName: 'CircleHelp' },
  { name: 'circle-minus', importName: 'CircleMinus' },
  { name: 'circle-percent', importName: 'CirclePercent' },
  { name: 'circle-plus', importName: 'CirclePlus' },
  { name: 'circle-x', importName: 'CircleX' },
  { name: 'circuit-board', importName: 'CircuitBoard' },
  { name: 'clock', importName: 'Clock' },
  { name: 'cloud', importName: 'Cloud' },
  { name: 'cloud-lightning', importName: 'CloudLightning' },
  { name: 'cloud-rain', importName: 'CloudRain' },
  { name: 'cloud-snow', importName: 'CloudSnow' },
  { name: 'cloud-sun', importName: 'CloudSun' },
  { name: 'coffee', importName: 'Coffee' },
  { name: 'cog', importName: 'Cog' },
  { name: 'coins', importName: 'Coins' },
  { name: 'compass', importName: 'Compass' },
  { name: 'computer', importName: 'Computer' },
  { name: 'container', importName: 'Container' },
  { name: 'cooking-pot', importName: 'CookingPot' },
  { name: 'cpu', importName: 'Cpu' },
  { name: 'credit-card', importName: 'CreditCard' },
  { name: 'cross', importName: 'Cross' },
  { name: 'crosshair', importName: 'Crosshair' },
  { name: 'crown', importName: 'Crown' },
  { name: 'cup-soda', importName: 'CupSoda' },
  { name: 'diamond', importName: 'Diamond' },
  { name: 'dollar-sign', importName: 'DollarSign' },
  { name: 'door-closed', importName: 'DoorClosed' },
  { name: 'door-open', importName: 'DoorOpen' },
  { name: 'download', importName: 'Download' },
  { name: 'droplet', importName: 'Droplet' },
  { name: 'droplets', importName: 'Droplets' },
  { name: 'dumbbell', importName: 'Dumbbell' },
  { name: 'ear', importName: 'Ear' },
  { name: 'earth', importName: 'Earth' },
  { name: 'expand', importName: 'Expand' },
  { name: 'eye', importName: 'Eye' },
  { name: 'eye-off', importName: 'EyeOff' },
  { name: 'factory', importName: 'Factory' },
  { name: 'fan', importName: 'Fan' },
  { name: 'feather', importName: 'Feather' },
  { name: 'filter', importName: 'Filter' },
  { name: 'fingerprint', importName: 'Fingerprint' },
  { name: 'fish', importName: 'Fish' },
  { name: 'flag', importName: 'Flag' },
  { name: 'flame', importName: 'Flame' },
  { name: 'flashlight', importName: 'Flashlight' },
  { name: 'flower', importName: 'Flower' },
  { name: 'focus', importName: 'Focus' },
  { name: 'footprints', importName: 'Footprints' },
  { name: 'fuel', importName: 'Fuel' },
  { name: 'funnel', importName: 'Funnel' },
  { name: 'gamepad', importName: 'Gamepad' },
  { name: 'gauge', importName: 'Gauge' },
  { name: 'gem', importName: 'Gem' },
  { name: 'gift', importName: 'Gift' },
  { name: 'glass-water', importName: 'GlassWater' },
  { name: 'glasses', importName: 'Glasses' },
  { name: 'globe', importName: 'Globe' },
  { name: 'gpu', importName: 'Gpu' },
  { name: 'grab', importName: 'Grab' },
  { name: 'hammer', importName: 'Hammer' },
  { name: 'hand', importName: 'Hand' },
  { name: 'handshake', importName: 'Handshake' },
  { name: 'hard-hat', importName: 'HardHat' },
  { name: 'headphones', importName: 'Headphones' },
  { name: 'heart', importName: 'Heart' },
  { name: 'heart-pulse', importName: 'HeartPulse' },
  { name: 'heater', importName: 'Heater' },
  { name: 'history', importName: 'History' },
  { name: 'home', importName: 'Home' },
  { name: 'hotel', importName: 'Hotel' },
  { name: 'hourglass', importName: 'Hourglass' },
  { name: 'infinity', importName: 'Infinity' },
  { name: 'info', importName: 'Info' },
  { name: 'key', importName: 'Key' },
  { name: 'keyboard', importName: 'Keyboard' },
  { name: 'lamp', importName: 'Lamp' },
  { name: 'laptop', importName: 'Laptop' },
  { name: 'layers', importName: 'Layers' },
  { name: 'leaf', importName: 'Leaf' },
  { name: 'life-buoy', importName: 'LifeBuoy' },
  { name: 'lightbulb', importName: 'Lightbulb' },
  { name: 'locate', importName: 'Locate' },
  { name: 'lock', importName: 'Lock' },
  { name: 'lock-open', importName: 'LockOpen' },
  { name: 'luggage', importName: 'Luggage' },
  { name: 'magnet', importName: 'Magnet' },
  { name: 'map', importName: 'Map' },
  { name: 'map-pin', importName: 'MapPin' },
  { name: 'medal', importName: 'Medal' },
  { name: 'megaphone', importName: 'Megaphone' },
  { name: 'memory-stick', importName: 'MemoryStick' },
  { name: 'mic', importName: 'Mic' },
  { name: 'microchip', importName: 'Microchip' },
  { name: 'microwave', importName: 'Microwave' },
  { name: 'monitor', importName: 'Monitor' },
  { name: 'monitor-smartphone', importName: 'MonitorSmartphone' },
  { name: 'moon', importName: 'Moon' },
  { name: 'mountain', importName: 'Mountain' },
  { name: 'mountain-snow', importName: 'MountainSnow' },
  { name: 'music', importName: 'Music' },
  { name: 'nfc', importName: 'Nfc' },
  { name: 'package', importName: 'Package' },
  { name: 'package-check', importName: 'PackageCheck' },
  { name: 'package-open', importName: 'PackageOpen' },
  { name: 'palette', importName: 'Palette' },
  { name: 'paw-print', importName: 'PawPrint' },
  { name: 'pencil-ruler', importName: 'PencilRuler' },
  { name: 'percent', importName: 'Percent' },
  { name: 'person-standing', importName: 'PersonStanding' },
  { name: 'phone', importName: 'Phone' },
  { name: 'piggy-bank', importName: 'PiggyBank' },
  { name: 'pill', importName: 'Pill' },
  { name: 'pin', importName: 'Pin' },
  { name: 'pipette', importName: 'Pipette' },
  { name: 'plane', importName: 'Plane' },
  { name: 'plug', importName: 'Plug' },
  { name: 'plug-zap', importName: 'PlugZap' },
  { name: 'power', importName: 'Power' },
  { name: 'power-off', importName: 'PowerOff' },
  { name: 'qr-code', importName: 'QrCode' },
  { name: 'radar', importName: 'Radar' },
  { name: 'radio', importName: 'Radio' },
  { name: 'recycle', importName: 'Recycle' },
  { name: 'refrigerator', importName: 'Refrigerator' },
  { name: 'rocket', importName: 'Rocket' },
  { name: 'route', importName: 'Route' },
  { name: 'router', importName: 'Router' },
  { name: 'ruler', importName: 'Ruler' },
  { name: 'satellite-dish', importName: 'SatelliteDish' },
  { name: 'scale', importName: 'Scale' },
  { name: 'scan', importName: 'Scan' },
  { name: 'scissors', importName: 'Scissors' },
  { name: 'search', importName: 'Search' },
  { name: 'search-check', importName: 'SearchCheck' },
  { name: 'settings', importName: 'Settings' },
  { name: 'shield', importName: 'Shield' },
  { name: 'shield-alert', importName: 'ShieldAlert' },
  { name: 'shield-check', importName: 'ShieldCheck' },
  { name: 'ship', importName: 'Ship' },
  { name: 'shirt', importName: 'Shirt' },
  { name: 'shopping-bag', importName: 'ShoppingBag' },
  { name: 'shopping-cart', importName: 'ShoppingCart' },
  { name: 'shower-head', importName: 'ShowerHead' },
  { name: 'signal', importName: 'Signal' },
  { name: 'signal-high', importName: 'SignalHigh' },
  { name: 'sliders-horizontal', importName: 'SlidersHorizontal' },
  { name: 'smartphone', importName: 'Smartphone' },
  { name: 'smile', importName: 'Smile' },
  { name: 'snowflake', importName: 'Snowflake' },
  { name: 'sofa', importName: 'Sofa' },
  { name: 'sparkles', importName: 'Sparkles' },
  { name: 'speaker', importName: 'Speaker' },
  { name: 'spray-can', importName: 'SprayCan' },
  { name: 'sprout', importName: 'Sprout' },
  { name: 'star', importName: 'Star' },
  { name: 'star-half', importName: 'StarHalf' },
  { name: 'stars', importName: 'Stars' },
  { name: 'stethoscope', importName: 'Stethoscope' },
  { name: 'store', importName: 'Store' },
  { name: 'sun', importName: 'Sun' },
  { name: 'sun-dim', importName: 'SunDim' },
  { name: 'sunrise', importName: 'Sunrise' },
  { name: 'sunset', importName: 'Sunset' },
  { name: 'tablet', importName: 'Tablet' },
  { name: 'tag', importName: 'Tag' },
  { name: 'tags', importName: 'Tags' },
  { name: 'target', importName: 'Target' },
  { name: 'telescope', importName: 'Telescope' },
  { name: 'tent', importName: 'Tent' },
  { name: 'thermometer', importName: 'Thermometer' },
  { name: 'thermometer-snowflake', importName: 'ThermometerSnowflake' },
  { name: 'thermometer-sun', importName: 'ThermometerSun' },
  { name: 'thumbs-down', importName: 'ThumbsDown' },
  { name: 'thumbs-up', importName: 'ThumbsUp' },
  { name: 'timer', importName: 'Timer' },
  { name: 'timer-reset', importName: 'TimerReset' },
  { name: 'train', importName: 'Train' },
  { name: 'tree-deciduous', importName: 'TreeDeciduous' },
  { name: 'tree-pine', importName: 'TreePine' },
  { name: 'trending-down', importName: 'TrendingDown' },
  { name: 'trending-up', importName: 'TrendingUp' },
  { name: 'trophy', importName: 'Trophy' },
  { name: 'truck', importName: 'Truck' },
  { name: 'tv', importName: 'Tv' },
  { name: 'umbrella', importName: 'Umbrella' },
  { name: 'unplug', importName: 'Unplug' },
  { name: 'usb', importName: 'Usb' },
  { name: 'utensils', importName: 'Utensils' },
  { name: 'verified', importName: 'Verified' },
  { name: 'video', importName: 'Video' },
  { name: 'volume', importName: 'Volume' },
  { name: 'volume-2', importName: 'Volume2' },
  { name: 'volume-off', importName: 'VolumeOff' },
  { name: 'volume-x', importName: 'VolumeX' },
  { name: 'wallet', importName: 'Wallet' },
  { name: 'warehouse', importName: 'Warehouse' },
  { name: 'washing-machine', importName: 'WashingMachine' },
  { name: 'watch', importName: 'Watch' },
  { name: 'waves', importName: 'Waves' },
  { name: 'weight', importName: 'Weight' },
  { name: 'wifi', importName: 'Wifi' },
  { name: 'wifi-off', importName: 'WifiOff' },
  { name: 'wind', importName: 'Wind' },
  { name: 'wine', importName: 'Wine' },
  { name: 'wrench', importName: 'Wrench' },
  { name: 'zap', importName: 'Zap' },
  { name: 'zoom-in', importName: 'ZoomIn' },
]

/** Set of valid kebab-case icon names for fast lookup */
export const LUCIDE_ICON_NAMES = new Set(
  LUCIDE_ICON_REGISTRY.map((entry) => entry.name)
)

export const FALLBACK_ICON_NAME = 'sparkles'

/** Validate an icon name against the registry, returning the fallback if invalid */
export function validateIconName(name: string): string {
  return LUCIDE_ICON_NAMES.has(name) ? name : FALLBACK_ICON_NAME
}

/** Comma-separated list of icon names for embedding in LLM prompts */
export const LUCIDE_ICON_NAMES_FOR_PROMPT = LUCIDE_ICON_REGISTRY.map(
  (entry) => entry.name
).join(', ')
